import { useCallback, useEffect, useMemo, useState } from 'react';
import * as settings from '../api/settings';
import * as userTeamsApi from '../api/userTeams';
import type { SettingsUser } from '../api/settings';
import type { UserTeam } from '../api/userTeams';
import { refreshUserTeams } from '../hooks/useUserTeams';
import { CreateUserTeamModal } from './CreateUserTeamModal';
import { SettingsToast } from './SettingsToast';

interface EditingRename {
  teamId: string;
  displayName: string;
  color: string;
}

/**
 * User-teams management tab. Lists every user-team (super-admin only) as a
 * card with rename/delete/add-member controls. Distinct from Companies:
 * user-teams are **named groupings for filtering**, not access partitions.
 *
 * Unlike CompaniesTab, we render real member chips on each card — the
 * `/user-teams` endpoint already returns members, so no extra call is
 * needed. Chips show displayName from `listUsers`, falling back to a
 * sub-short-hash when a user profile isn't found (disabled users, pruned
 * profiles, etc).
 */
export function UserTeamsTab() {
  const [teams, setTeams] = useState<UserTeam[]>([]);
  const [users, setUsers] = useState<SettingsUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [addingMemberFor, setAddingMemberFor] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<EditingRename | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [ts, us] = await Promise.all([
        userTeamsApi.listUserTeams(),
        settings.listUsers(false),
      ]);
      setTeams(ts);
      setUsers(us);
      setError(null);
      // Keep the module-level cache fresh so the ViewerToggle reflects edits.
      await refreshUserTeams().catch(() => {});
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const sorted = useMemo(
    () => [...teams].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [teams]
  );

  const usersById = useMemo(() => {
    const map = new Map<string, SettingsUser>();
    for (const u of users) map.set(u.userId, u);
    return map;
  }, [users]);

  const onDelete = useCallback(
    async (t: UserTeam) => {
      if (!window.confirm(`Delete team "${t.displayName}"? This cannot be undone.`)) return;
      setBusyId(t.teamId);
      try {
        await userTeamsApi.deleteUserTeam(t.teamId);
        setToast(`Deleted ${t.displayName}`);
        await refresh();
      } catch (err) {
        alert((err as Error).message);
      } finally {
        setBusyId(null);
      }
    },
    [refresh]
  );

  const onAddMember = useCallback(
    async (teamId: string, sub: string) => {
      setBusyId(teamId);
      try {
        await userTeamsApi.addUserTeamMember(teamId, sub);
        const user = users.find((u) => u.userId === sub);
        setToast(`Added ${user?.displayName ?? 'user'}`);
        setAddingMemberFor(null);
        await refresh();
      } catch (err) {
        alert((err as Error).message);
      } finally {
        setBusyId(null);
      }
    },
    [refresh, users]
  );

  const onRemoveMember = useCallback(
    async (teamId: string, sub: string) => {
      setBusyId(teamId);
      try {
        await userTeamsApi.removeUserTeamMember(teamId, sub);
        const user = users.find((u) => u.userId === sub);
        setToast(`Removed ${user?.displayName ?? 'user'}`);
        await refresh();
      } catch (err) {
        alert((err as Error).message);
      } finally {
        setBusyId(null);
      }
    },
    [refresh, users]
  );

  const onSaveRename = useCallback(async () => {
    if (!renaming) return;
    const { teamId, displayName, color } = renaming;
    const name = displayName.trim();
    if (!name) return;
    setBusyId(teamId);
    try {
      await userTeamsApi.updateUserTeam(teamId, { displayName: name, color });
      setToast(`Renamed to ${name}`);
      setRenaming(null);
      await refresh();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }, [renaming, refresh]);

  return (
    <div className="settings-tab-body">
      <div className="settings-tab-toolbar">
        <span className="muted small">
          {teams.length} {teams.length === 1 ? 'team' : 'teams'}
        </span>
        <button
          type="button"
          className="primary"
          onClick={() => setCreateOpen(true)}
        >
          + New team
        </button>
      </div>

      {loading ? (
        <div className="settings-loading muted">Loading teams…</div>
      ) : error ? (
        <div className="settings-error">{error}</div>
      ) : sorted.length === 0 ? (
        <div className="settings-empty muted">
          No teams yet — create your first. Teams group users for filtering
          the task list; they don't control task access.
        </div>
      ) : (
        <div className="settings-company-grid">
          {sorted.map((t) => {
            const memberCount = t.members.length;
            const canDelete = memberCount === 0;
            const busy = busyId === t.teamId;
            const isRenaming = renaming?.teamId === t.teamId;
            const isAddingMember = addingMemberFor === t.teamId;
            const nonMembers = users.filter((u) => !t.members.includes(u.userId));

            return (
              <div key={t.teamId} className="settings-company-card">
                <div className="settings-company-card-head">
                  {isRenaming ? (
                    <div className="settings-company-rename">
                      <input
                        type="color"
                        value={renaming.color}
                        onChange={(e) =>
                          setRenaming({ ...renaming, color: e.target.value })
                        }
                      />
                      <input
                        type="text"
                        value={renaming.displayName}
                        onChange={(e) =>
                          setRenaming({ ...renaming, displayName: e.target.value })
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void onSaveRename();
                          if (e.key === 'Escape') setRenaming(null);
                        }}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <>
                      <span
                        className="settings-color-dot settings-color-dot-lg"
                        style={{ backgroundColor: t.color }}
                        aria-hidden="true"
                      />
                      <div className="settings-company-name">
                        <strong>{t.displayName}</strong>
                        <span className="muted small">{t.teamId}</span>
                      </div>
                    </>
                  )}
                </div>

                <div className="settings-company-meta">
                  <span className="settings-chip">
                    {memberCount} {memberCount === 1 ? 'member' : 'members'}
                  </span>
                </div>

                {memberCount > 0 && (
                  <div className="settings-chip-row">
                    {t.members.map((sub) => {
                      const u = usersById.get(sub);
                      const label = u?.displayName ?? `${sub.slice(0, 8)}…`;
                      const chipColor = u?.color ?? '#64748b';
                      return (
                        <span key={sub} className="settings-chip">
                          <span
                            className="settings-color-dot"
                            style={{ backgroundColor: chipColor }}
                            aria-hidden="true"
                          />
                          {label}
                          <button
                            type="button"
                            className="settings-chip-x"
                            aria-label={`Remove ${label}`}
                            disabled={busy}
                            onClick={() => void onRemoveMember(t.teamId, sub)}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {isAddingMember && (
                  <div className="settings-add-member">
                    <span className="settings-field-label">Add member</span>
                    <div className="settings-chip-row">
                      {nonMembers.length === 0 ? (
                        <span className="muted small">All users already in team.</span>
                      ) : (
                        nonMembers.map((u) => (
                          <button
                            key={u.userId}
                            type="button"
                            className="settings-chip-button"
                            disabled={busy}
                            onClick={() => void onAddMember(t.teamId, u.userId)}
                          >
                            <span
                              className="settings-color-dot"
                              style={{ backgroundColor: u.color }}
                              aria-hidden="true"
                            />
                            {u.displayName}
                          </button>
                        ))
                      )}
                    </div>
                    <button
                      type="button"
                      className="ghost small"
                      onClick={() => setAddingMemberFor(null)}
                    >
                      Cancel
                    </button>
                  </div>
                )}

                <div className="settings-company-actions">
                  {isRenaming ? (
                    <>
                      <button
                        type="button"
                        className="primary"
                        disabled={busy}
                        onClick={() => void onSaveRename()}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => setRenaming(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          setRenaming({
                            teamId: t.teamId,
                            displayName: t.displayName,
                            color: t.color,
                          })
                        }
                      >
                        Rename
                      </button>
                      {!isAddingMember && (
                        <button
                          type="button"
                          onClick={() => setAddingMemberFor(t.teamId)}
                        >
                          + Add member
                        </button>
                      )}
                      <button
                        type="button"
                        className="danger"
                        disabled={busy || !canDelete}
                        title={
                          !canDelete
                            ? 'Remove members first'
                            : undefined
                        }
                        onClick={() => void onDelete(t)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CreateUserTeamModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onDone={async (msg) => {
          setCreateOpen(false);
          setToast(msg);
          await refresh();
        }}
      />
      <SettingsToast message={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
